/**
 * Search Relevance Engine – Intent, normalization, aliases, scoring.
 * Used only for ranking/sorting; no API or booking flow changes.
 */

export const INTENT_HOTEL_SEARCH = 'HOTEL_SEARCH';
export const INTENT_DESTINATION_SEARCH = 'DESTINATION_SEARCH';

const MAX_WORDS_HOTEL_QUERY = 4;

const HOTEL_BRAND_WORDS = [
  'sheraton', 'hilton', 'hyatt', 'marriott', 'radisson', 'dan', 'isrotel',
  'שרתון', 'הילטון', 'היאט', 'מריוט', 'רדיסון', 'דן', 'ישרוטל',
  'intercontinental', 'crown', 'royal', 'grand', 'plaza', 'tower', 'resort',
  'אינטרקונטיננטל', 'קראון', 'רויאל', 'גארדן'
];

const DESTINATION_PATTERNS = [
  /\b(מלונות|hotels?)\s+(ב|in|at)\s+/i,
  /\b(ב|in)\s+(אתונה|athens|תל אביב|tel aviv|פריז|paris)\b/i,
  /^(אתונה|athens|פריז|paris|תל אביב|tel aviv)\s*$/i,
];

const GENERIC_WORDS = ['hotel', 'מלון', 'resort', 'the', 'of', 'and', 'in', 'on', 'at', 'ה'];

/**
 * Canonical name -> list of alternate names (aliases).
 * All keys and values should be normalized with normalizeHotelName for comparison.
 */
export const hotelAliases = {
  'sheraton grand tel aviv': ['sheraton tel aviv', 'sheraton tlv', 'שרתון תל אביב', 'שרתון גרנד תל אביב'],
  'sheraton tel aviv': ['sheraton grand tel aviv', 'sheraton tlv', 'שרתון תל אביב'],
  'hilton tel aviv': ['hilton tlv', 'הילטון תל אביב'],
  'hilton athens': ['הילטון אתונה'],
  'dan panorama eilat': ['dan panorama', 'דן פנורמה אילת'],
  'dan tel aviv': ['דן תל אביב'],
  // AC Hotel by Marriott Miami Aventura – canonical normalizes to "ac by marriott miami aventura"; add short queries so EXACT/PHRASE win
  'ac by marriott miami aventura': [
    'ac aventura',
    'ac hotel aventura',
    'ac hotel miami aventura',
    'ac marriott aventura',
    'ac hotel by marriott aventura',
  ],
  // "X by Marriott/Hilton [city]" – short queries get only TOKEN without aliases (see docs/HOTEL_ALIAS_GAP_ANALYSIS.md)
  'w by marriott barcelona': ['w barcelona', 'w hotel barcelona', 'w marriott barcelona'],
  'doubletree by hilton paris': ['doubletree paris', 'doubletree hilton paris', 'doubletree hotel paris'],
  'courtyard by marriott new york': ['courtyard new york', 'courtyard ny', 'courtyard marriott new york'],
  'curio collection by hilton london': ['curio london', 'curio hilton london', 'curio collection london'],
  'hilton garden inn tel aviv': ['hilton tel aviv', 'hilton tlv', 'hilton garden inn tlv'],
  'jw marriott miami': ['jw miami', 'jw marriott miami'],
  'aloft miami brickell': ['aloft brickell', 'aloft miami'],
  'queen sheba eilat': ['מלכת שבא', 'מלון מלכת שבא', 'queen of sheba', 'queen of sheba eilat'],
};

let _aliasToCanonical = null;

function buildAliasToCanonical() {
  if (_aliasToCanonical) return _aliasToCanonical;
  const m = new Map();
  for (const [canonical, list] of Object.entries(hotelAliases)) {
    const canonNorm = normalizeHotelName(canonical);
    m.set(canonNorm, canonNorm);
    (list || []).forEach((alias) => {
      const a = normalizeHotelName(alias);
      if (a && !m.has(a)) m.set(a, canonNorm);
    });
  }
  _aliasToCanonical = m;
  return _aliasToCanonical;
}

/**
 * Normalize hotel name for comparison: lowercase, single spaces, no punctuation, no generic words.
 * @param {string} name
 * @returns {string}
 */
export function normalizeHotelName(name) {
  if (name == null || typeof name !== 'string') return '';
  let s = name
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  GENERIC_WORDS.forEach((word) => {
    const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    s = s.replace(re, ' ').replace(/\s+/g, ' ').trim();
  });
  return s;
}

/**
 * Get all normalized names that should match this hotel (canonical + aliases).
 * @param {string} normalizedDisplayName - already normalized DisplayName
 * @returns {string[]}
 */
export function getHotelMatchNames(normalizedDisplayName) {
  if (!normalizedDisplayName) return [];
  const canon = buildAliasToCanonical().get(normalizedDisplayName) || normalizedDisplayName;
  const list = [canon];
  const aliases = hotelAliases[canon];
  if (Array.isArray(aliases)) {
    aliases.forEach((a) => {
      const n = normalizeHotelName(a);
      if (n && !list.includes(n)) list.push(n);
    });
  }
  return list;
}

/**
 * Detect search intent from raw query (and optional existing hotelName from location).
 * @param {string} query - raw user input (e.g. location.fullname or location.hotelserched)
 * @param {string} [existingHotelName] - if already set from location.hotelserched
 * @returns {'HOTEL_SEARCH'|'DESTINATION_SEARCH'}
 */
export function detectSearchIntent(query, existingHotelName) {
  if (existingHotelName && String(existingHotelName).trim().length > 0) {
    return INTENT_HOTEL_SEARCH;
  }
  const q = (query == null ? '' : String(query)).trim();
  if (!q) return INTENT_DESTINATION_SEARCH;

  const normalized = normalizeHotelName(q);
  const words = normalized.split(/\s+/).filter(Boolean);

  if (words.length > MAX_WORDS_HOTEL_QUERY) {
    return INTENT_DESTINATION_SEARCH;
  }

  const hasDestinationPattern = DESTINATION_PATTERNS.some((re) => re.test(q));
  if (hasDestinationPattern) return INTENT_DESTINATION_SEARCH;

  const hasBrand = HOTEL_BRAND_WORDS.some((brand) =>
    normalized.includes(brand)
  );
  if (hasBrand) return INTENT_HOTEL_SEARCH;

  const hasHotelLike = /(grand|plaza|tower|resort|suite|inn|מלון|בוטיק)/i.test(q);
  if (hasHotelLike && words.length <= 4) return INTENT_HOTEL_SEARCH;

  return INTENT_DESTINATION_SEARCH;
}

/**
 * Text match tiers: 0 exact, 1 phrase, 2 prefix, 3 keyword, 4 partial, 5 no match.
 * @param {string} searchNormalized
 * @param {string} hotelDisplayName
 * @param {string[]} hotelMatchNames - [normalizedDisplayName, ...aliases]
 * @returns {{ score: number, exact: boolean }}
 */
export function calculateTextMatchScore(searchNormalized, hotelDisplayName, hotelMatchNames) {
  if (!searchNormalized || !hotelMatchNames.length) {
    return { score: 5, exact: false };
  }

  const displayLower = (hotelDisplayName || '').toLowerCase();
  const searchNorm = normalizeHotelName(searchNormalized);

  const exactMatch = hotelMatchNames.some((n) => n && n === searchNorm);
  if (exactMatch) return { score: 0, exact: true };

  const phraseMatch = hotelMatchNames.some(
    (n) =>
      n && searchNorm && (n.includes(searchNorm) || searchNorm.includes(n)) &&
      Math.min(n.length, searchNorm.length) >= 4
  );
  if (phraseMatch) return { score: 1, exact: false };

  const prefixMatch = hotelMatchNames.some(
    (n) =>
      n && searchNorm && (n.startsWith(searchNorm) || searchNorm.startsWith(n)) &&
      searchNorm.length >= 2
  );
  if (prefixMatch) return { score: 2, exact: false };

  const searchWords = searchNorm.split(/\s+/).filter((w) => w.length >= 2);
  const keywordMatch = hotelMatchNames.some((n) => {
    if (!n) return false;
    const matched = searchWords.filter((w) => n.includes(w));
    return matched.length >= 1 && matched.length / Math.max(searchWords.length, 1) >= 0.5;
  });
  if (keywordMatch) return { score: 3, exact: false };

  const partialMatch = hotelMatchNames.some(
    (n) => n && searchNorm && (n.includes(searchNorm) || searchNorm.includes(n))
  );
  if (partialMatch) return { score: 4, exact: false };

  return { score: 5, exact: false };
}

const EXACT_MATCH_BOOST = 100;
const TEXT_WEIGHT = 100;
const DISTANCE_WEIGHT = 5;

/**
 * Relevance score (lower = better). Formula:
 * textMatchScore * 100 + distanceKm * 5 - popularityBoost - (exactMatch ? 100 : 0)
 * @param {number} textMatchScore - 0..5
 * @param {number} distanceKm - distance in km (can be null/undefined -> 0)
 * @param {number} [popularityBoost] - optional positive number to subtract
 * @param {boolean} [isExactMatch]
 * @returns {number}
 */
export function calculateRelevanceScore(textMatchScore, distanceKm, popularityBoost = 0, isExactMatch = false) {
  const dist = distanceKm != null && !Number.isNaN(Number(distanceKm)) ? Number(distanceKm) : 0;
  let score =
    (textMatchScore ?? 5) * TEXT_WEIGHT +
    dist * DISTANCE_WEIGHT -
    (popularityBoost || 0);
  if (isExactMatch || textMatchScore === 0) {
    score -= EXACT_MATCH_BOOST;
  }
  return score;
}

/**
 * Key for deduplication: prefer ID, then normalized DisplayName.
 * @param {object} hotel
 * @returns {string}
 */
export function getDeduplicationKey(hotel) {
  if (!hotel) return '';
  const id = hotel.ID ?? hotel.Id ?? hotel.id;
  if (id != null && String(id).trim() !== '') return `id:${id}`;
  const name = hotel.DisplayName ?? hotel.Name ?? hotel.HotelName ?? '';
  const norm = normalizeHotelName(name);
  return norm ? `name:${norm}` : `rand:${Math.random()}`;
}

/**
 * LowestPackagePrice from searchHotels / composite list rows — raw API numeric (dedup / sort only).
 */
export function parseLowestPackagePriceUsd(raw) {
  if (raw == null || raw === '') return null;
  const n =
    typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * One row per hotel after dedup: prefer the cheapest valid LowestPackagePrice; tie-break by lower relevanceScore.
 * Ensures the card/list "החל מ" reflects the minimum price returned for that hotel identity in the raw result set.
 */
export function pickBetterHotelRowForListDedup(incoming, current) {
  if (!current) return incoming;
  if (!incoming) return current;
  const pIn = parseLowestPackagePriceUsd(incoming.LowestPackagePrice);
  const pCur = parseLowestPackagePriceUsd(current.LowestPackagePrice);
  let winner;
  if (pIn != null && pCur == null) winner = incoming;
  else if (pIn == null && pCur != null) winner = current;
  else if (pIn != null && pCur != null && pIn !== pCur) {
    winner = pIn < pCur ? incoming : current;
  } else {
    const sIn =
      incoming.relevanceScore != null ? incoming.relevanceScore : Infinity;
    const sCur =
      current.relevanceScore != null ? current.relevanceScore : Infinity;
    winner = sIn < sCur ? incoming : current;
  }
  const hrIn = incoming._hr_sort_index;
  const hrCur = current._hr_sort_index;
  const nums = [hrIn, hrCur].filter(
    (x) => x != null && Number.isFinite(Number(x))
  );
  if (!nums.length) return winner;
  return { ...winner, _hr_sort_index: Math.min(...nums.map(Number)) };
}
